import React, { useEffect, useRef } from "react";

// Path definitions precisely calibrated to the 1024x665 hero.png artwork
// Each path starts at the central AI cube/chip and ends at the building connection port
const NETWORK_CONNECTIONS = [
  {
    id: "top",
    name: "North Skyscraper",
    // Smooth cubic bezier from AI cube top-right face to skyscraper base port
    d: "M 646 260 C 692 252, 690 210, 645 198",
  },
  {
    id: "upper_left",
    name: "Upper Left Enterprise",
    // Straight line from AI chip edge to platform port
    d: "M 534 342 L 448 298",
  },
  {
    id: "lower_left",
    name: "Lower Left Center",
    // Straight line from AI chip edge to platform port
    d: "M 538 396 L 450 431",
  },
  {
    id: "upper_right",
    name: "Upper Right Complex",
    // Straight line from AI chip edge to platform port
    d: "M 662 319 L 765 260",
  },
  {
    id: "lower_right",
    name: "Lower Right Facility",
    // Straight line from AI chip edge to platform port
    d: "M 642 385 L 768 447",
  },
];

// Launch schedule as required:
// Point 1: t = 0s
// Point 2: t = 1s
// Point 3: t = 2s
const LAUNCH_DELAYS = [0.0, 1.0, 2.0]; // in seconds
const TRAVEL_DURATION = 2.0; // 2 seconds per journey
const CYCLE_DURATION = 5.0; // 4s journey end + 1s rest before next wave

const HeroNetworkAnimation = () => {
  const svgRef = useRef(null);
  const pathsRef = useRef({});
  const pointsRef = useRef({});

  useEffect(() => {
    // Check reduced motion preference
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    // Cache total length for each path
    const pathLengths = {};
    NETWORK_CONNECTIONS.forEach((conn) => {
      const pathEl = pathsRef.current[conn.id];
      if (pathEl) {
        pathLengths[conn.id] = pathEl.getTotalLength();
      }
    });

    let animationFrameId;
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsedTotal = (timestamp - startTime) / 1000; // in seconds
      const cycleTime = elapsedTotal % CYCLE_DURATION; // 0 to CYCLE_DURATION

      NETWORK_CONNECTIONS.forEach((conn) => {
        const pathEl = pathsRef.current[conn.id];
        const totalLen = pathLengths[conn.id];
        if (!pathEl || !totalLen) return;

        LAUNCH_DELAYS.forEach((launchTime, ptIdx) => {
          const ptGroup = pointsRef.current[`${conn.id}_${ptIdx}`];
          if (!ptGroup) return;

          const timeSinceLaunch = cycleTime - launchTime;

          if (timeSinceLaunch < 0 || timeSinceLaunch > TRAVEL_DURATION) {
            // Not active yet or already finished this cycle
            ptGroup.style.opacity = "0";
            return;
          }

          // Normalized progress along the line: strictly [0, 1]
          const progress = Math.min(1, Math.max(0, timeSinceLaunch / TRAVEL_DURATION));

          // Calculate exact position along the existing line path
          const pos = pathEl.getPointAtLength(progress * totalLen);

          // Smooth fade-in at AI cube (0 -> 0.08) and smooth fade-out near building (0.85 -> 1.0)
          let opacity = 1.0;
          if (progress < 0.08) {
            opacity = progress / 0.08;
          } else if (progress > 0.85) {
            opacity = Math.max(0, (1.0 - progress) / 0.15);
          }

          // Direct DOM transformation for silky 60/120fps performance without React re-renders
          ptGroup.setAttribute("transform", `translate(${pos.x.toFixed(2)}, ${pos.y.toFixed(2)})`);
          ptGroup.style.opacity = opacity.toFixed(3);
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1024 665"
      className="absolute inset-0 w-full h-full pointer-events-none z-[5]"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {/* Futuristic electric cyan glow filter */}
        <filter id="neon-packet-glow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur2" />
          <feMerge>
            <feMergeNode in="blur2" />
            <feMergeNode in="blur1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Invisible calibrated paths for coordinate tracking */}
      {NETWORK_CONNECTIONS.map((conn) => (
        <path
          key={conn.id}
          ref={(el) => (pathsRef.current[conn.id] = el)}
          d={conn.d}
          fill="none"
          stroke="transparent"
          strokeWidth="1"
        />
      ))}

      {/* Animated Glowing Points (3 points per connection) */}
      {NETWORK_CONNECTIONS.map((conn) =>
        LAUNCH_DELAYS.map((_, ptIdx) => (
          <g
            key={`${conn.id}_${ptIdx}`}
            ref={(el) => (pointsRef.current[`${conn.id}_${ptIdx}`] = el)}
            style={{ opacity: 0, willChange: "transform, opacity" }}
          >
            {/* Outer Electric Cyan / Light Blue Halo (#00BFFF) */}
            <circle
              r="6.5"
              fill="#00BFFF"
              opacity="0.45"
              filter="url(#neon-packet-glow)"
            />
            {/* Mid Light Cyan Glow (#80E5FF) */}
            <circle
              r="4"
              fill="#80E5FF"
              opacity="0.85"
            />
            {/* Pure White Hot Center (#FFFFFF) */}
            <circle
              r="2.2"
              fill="#FFFFFF"
            />
          </g>
        ))
      )}
    </svg>
  );
};

export default HeroNetworkAnimation;
